import DOMPurify from 'dompurify';

// The inbox `body` field is HTML rendered by the notification service. Any channel the backend
// adds — including ones this frontend has never seen — can put markup in front of every user, so
// the server-side escaping guarantee is not treated as sufficient on its own. This allowlist is
// deliberately narrower than DOMPurify's defaults: only inline formatting and links survive.
const ALLOWED_TAGS = ['b', 'strong', 'i', 'em', 'u', 'span', 'br', 'a'];
const ALLOWED_ATTR = ['href', 'title', 'target'];
const ALLOWED_URI_REGEXP = /^(?:https?:|mailto:)/i;

let purifier: ReturnType<typeof DOMPurify> | undefined;

// Built on first use rather than at import: without a DOM, DOMPurify exports a stub that has no
// addHook, so doing this at module scope throws for anything that merely imports this file. The
// instance is our own so the hook below stays off every unrelated sanitize call in the app.
function getPurifier() {
  if (purifier) {
    return purifier;
  }
  purifier = DOMPurify(window);

  // A link the backend marks target="_blank" would otherwise hand window.opener to the linked page
  purifier.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });

  return purifier;
}

export function sanitizeNotificationHtml(html: string): string {
  return getPurifier().sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOWED_URI_REGEXP,
    // ALLOWED_URI_REGEXP is matched against every attribute value, so target="_blank" would be
    // dropped as a malformed URL unless it is marked as carrying no URI
    ADD_URI_SAFE_ATTR: ['target'],
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
  });
}
